import { Ref } from "react";

import { BaseRHFWidgetProps } from "@impero-com/common/components/form/rhfFields/types";
import { FieldValues, useController } from "react-hook-form";
import { ZodString } from "zod";

import {
    MarkdownEditorMethods,
    MarkdownSelectionRange,
    SourceToggleMode,
} from "components/markdownEditor/types";

import { MarkdownInput, MarkdownInputBaseProps } from "../fields/markdownInput";

interface RHFMarkdownInputProps<TFieldsValues extends FieldValues>
    extends BaseRHFWidgetProps<TFieldsValues, string>, MarkdownInputBaseProps {
    schema?: ZodString;
    onBlur?: (value: string) => void;
    sourceToggleMode?: SourceToggleMode;
    headerAction?: React.ReactNode;
    onSelect?: (selection?: MarkdownSelectionRange) => void;
    keepEditorOpen?: boolean;
    editorRef?: Ref<MarkdownEditorMethods>;
}

export function RHFMarkdownInput<TFieldsValues extends FieldValues>({
    label,
    placeholder,
    name,
    control,
    disabled,
    styleVariant,
    autoFocus,
    onBlur,
    sourceToggleMode,
    headerAction,
    onSelect,
    keepEditorOpen,
    editorRef,
}: RHFMarkdownInputProps<TFieldsValues>) {
    const { field, fieldState } = useController({ name, control, disabled });

    return (
        <MarkdownInput
            label={label}
            value={field.value}
            onChange={field.onChange}
            onBlur={() => {
                onBlur?.(field.value);
                field.onChange(field.value);
                field.onBlur();
            }}
            error={fieldState.error?.message}
            placeholder={placeholder}
            disabled={field.disabled}
            styleVariant={styleVariant}
            autoFocus={autoFocus}
            ref={editorRef}
            fieldRef={field.ref}
            sourceToggleMode={sourceToggleMode}
            headerAction={headerAction}
            onSelect={onSelect}
            keepEditorOpen={keepEditorOpen}
        />
    );
}
